import * as React from "react";
import { bindActionCreators } from "redux";
import styled from "styled-components";
import { connect } from "react-redux";
import moment, { Moment } from "moment";
import { withRouter } from "react-router";

import withGranted, { IWithGrantedProps } from "../HOC/WithGranted";
import { createStructuredSelector } from "reselect";
import { IGlobalStore } from "../../_reducers/reducers";
import { IShortLocationInfo } from "../../_types/common";
import * as actionsSearch from "../../_actions/search";
import * as layoutActions from "../../_actions/layout";
import * as invoicesApi from "../../_api/invoices";
import { notifySwagger, notify } from "../../_actions/notification";

import {
  DatePicker,
  Table,
  LocationSelectorComponent,
  Pagination,
  MainContainer,
  RunningLineLoader,
  Skeleton,
  Button,
  Article
} from "../../components";
import { IFlatLocationOption } from "../../_reducers/current-user";
import { clientUnitFilterOptions } from "../../_utils/clientUnitFilter";

const Position = {
  Container: styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
  `,

  Body: styled.div`
    height: 100%;
    flex: 1 1 100%;
  `,

  Footer: styled.div`
    flex: 0 0;
  `,

  Head: styled<{ className?: string }>(({ className, children }) => (
    <header className={className}>
      <div>{children}</div>
    </header>
  ))`
    padding: 20px;
    > div {
      margin: -7.5px;
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
    }
  `,

  HeadElement: styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    & > * {
      margin: 7.5px;
    }
  `,

  Filter: styled.div`
    width: 260px;
  `,

  Toolbar: styled.div`
    display: flex;
    align-items: center;
    height: 100%;
    width: 100%;
    justify-content: flex-end;
    > * {
      margin-left: 10px;
    }
  `
};

interface IGetPartialShipmentsParams {
  startDate?: moment.Moment;
  endDate?: moment.Moment;
  sorting?: string;
  skip?: number;
  perPage?: number;
}

interface IPartialShipment {
  id: number;
  invoiceDate?: string;
  ottoPoNumber?: string;
  invoiceNumber?: string;
  productSku?: string;
  vendorName?: string;
  ottoQty?: number;
  vendorQty?: number;
  ottoPrice?: number;
  vendorPrice?: number;
  quantityDifference?: number;
  quantityDifferencePercentage?: number;
}

interface IPartialShipmentsListProps
  extends IWithGrantedProps,
    IConnectedProps,
    IConnectedActions {}

interface IPartialShipmentsListState {
  sorting: string;
  skip: number;
  partialShipmentsStatus: string;
  partialShipments?: IPartialShipment[];
  totalCount: number;
  clientUnitFilter: number | null;
  vendorFilter: number | null;
  startDate: Moment | null;
  endDate: Moment | null;
  focusedInput: string;
  isExporting: boolean;
  isResolving: { [id: number]: boolean };
}

class PartialShipmentsList extends React.Component<
  IPartialShipmentsListProps,
  IPartialShipmentsListState
> {
  static renderPreloader(perPage: number, status: string): React.Component[] {
    const rows = [];
    const elementsCount = status === "loading" ? perPage : 0;

    for (let i = 0; i < elementsCount; i++) {
      rows.push(
        <Table.Row
          key={i}
          rows={perPage}
          cells={[
            <Skeleton.Line key={`partial-shipments-preloader-${i}-0`} size="xs" />,
            <Skeleton.Line key={`partial-shipments-preloader-${i}-1`} size="sm" />,
            <Skeleton.Line key={`partial-shipments-preloader-${i}-2`} size="sm" />,
            <Skeleton.Line key={`partial-shipments-preloader-${i}-3`} size="sm" />,
            <Skeleton.Line key={`partial-shipments-preloader-${i}-4`} size="sm" />,
            <Skeleton.Line key={`partial-shipments-preloader-${i}-5`} size="xs" />,
            <Skeleton.Line key={`partial-shipments-preloader-${i}-6`} size="xs" />,
            <Skeleton.Line key={`partial-shipments-preloader-${i}-7`} size="xs" />,
            <Skeleton.Line key={`partial-shipments-preloader-${i}-8`} size="sm" />
          ]}
        />
      );
    }
    return rows;
  }

  constructor(props) {
    super(props);

    this.state = {
      sorting: "invoiceDate DESC",
      skip: 0,
      partialShipmentsStatus: "",
      totalCount: 0,
      clientUnitFilter: null,
      vendorFilter: null,
      startDate: null,
      endDate: null,
      focusedInput: null,
      isExporting: false,
      isResolving: {}
    };

    this.handleChangePage = this.handleChangePage.bind(this);
    this.handleSortChanged = this.handleSortChanged.bind(this);
    this.getPartialShipments = this.getPartialShipments.bind(this);
    this.handleClientFilterChange = this.handleClientFilterChange.bind(this);
    this.handleDateChanged = this.handleDateChanged.bind(this);
    this.handleExportClick = this.handleExportClick.bind(this);
    this.handleResolveClick = this.handleResolveClick.bind(this);

    this.props.setSearch("/invoices/partial-shipments", () => {
      this.setState({ skip: 0 }, () => {
        this.getPartialShipments();
      });
    });
  }

  componentDidMount() {
    this.getPartialShipments();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.locationInfo.id !== this.props.locationInfo.id) {
      this.setState({ clientUnitFilter: null }, () => {
        this.getPartialShipments();
      });
    }
  }

  getPartialShipments(params: IGetPartialShipmentsParams = {}) {
    const skip =
      Number.isInteger(params.skip) && params.skip >= 0
        ? params.skip
        : this.state.skip;
    const sort = params.sorting || this.state.sorting;
    const startDate = params.startDate !== undefined ? params.startDate : this.state.startDate;
    const endDate = params.endDate !== undefined ? params.endDate : this.state.endDate;
    const perPage = params.perPage || this.props.perPage;

    let statusStartKey = "loading";
    let statusFinishKey = "loaded";
    if (params.sorting) {
      statusStartKey = "sorting";
      statusFinishKey = "sorted";
    }

    this.setState({ partialShipmentsStatus: statusStartKey });

    console.log('=== getPartialShipments called ===');
    console.log('clientUnitFilter:', this.state.clientUnitFilter);
    console.log('startDate:', startDate);
    console.log('endDate:', endDate);

    invoicesApi
      .getPartialShipments(
        startDate,
        endDate,
        this.state.clientUnitFilter,
        null,
        skip,
        perPage,
        sort
      )
      .then((response: any) => {
        this.setState({
          partialShipmentsStatus: statusFinishKey,
          partialShipments: response.items,
          totalCount: response.totalCount
        });
      })
      .catch(e => {
        this.props.notifySwagger(e, "error");
        this.setState({ partialShipmentsStatus: "error" });
      });
  }

  handleChangePage(pageInfo) {
    const { perPage, skip } = pageInfo;

    this.setState({ skip }, () => {
      this.getPartialShipments({ perPage, skip });
      this.props.setPerPage(perPage);
    });
  }

  handleSortChanged(sorting) {
    this.setState({ sorting });
    this.getPartialShipments({ sorting });
  }

  handleClientFilterChange(value: IFlatLocationOption) {
    this.setState({ clientUnitFilter: value?.id || null, skip: 0 }, () => {
      this.getPartialShipments();
    });
  }

  handleDateChanged(range: { startDate: Moment; endDate: Moment }) {
    const startDate = range.startDate
      ? range.startDate.startOf("day")
      : null;
    const endDate = range.endDate ? range.endDate.startOf("day") : null;

    this.setState({ startDate, endDate, skip: 0 }, () => {
      this.getPartialShipments();
    });
  }

  handleExportClick() {
    this.setState({ isExporting: true });

    invoicesApi
      .exportPartialShipmentsToExcel(
        this.state.startDate,
        this.state.endDate,
        this.state.clientUnitFilter,
        null
      )
      .then(() => {
        this.setState({ isExporting: false });
        this.props.notify("Partial shipments exported successfully!", "success");
      })
      .catch(err => {
        this.setState({ isExporting: false });
        if (typeof err === "string") {
          this.props.notify(err, "error");
        } else {
          this.props.notify("Error exporting partial shipments", "error");
        }
      });
  }

  handleResolveClick(shipmentId: number) {
    this.setState(prevState => ({
      isResolving: { ...prevState.isResolving, [shipmentId]: true }
    }));

    invoicesApi
      .resolvePartialShipment(shipmentId)
      .then(() => {
        this.setState(prevState => ({
          isResolving: { ...prevState.isResolving, [shipmentId]: false },
          partialShipments: prevState.partialShipments.filter(s => s.id !== shipmentId),
          totalCount: prevState.totalCount - 1
        }));
        this.props.notify("Partial shipment resolved successfully!", "success");
      })
      .catch(err => {
        this.setState(prevState => ({
          isResolving: { ...prevState.isResolving, [shipmentId]: false }
        }));
        this.props.notifySwagger(err, "error");
      });
  }

  render() {
    const hasData = this.state.partialShipments && this.state.partialShipments.length > 0;
    const isLoaded = this.state.partialShipmentsStatus === "loaded" || 
                     this.state.partialShipmentsStatus === "sorted";

    return (
      <MainContainer>
        <Position.Container>
          <Position.Head>
            <Position.HeadElement>
              <Article.Text>
                <strong>VENDOR PARTIAL SHIPMENTS</strong>
              </Article.Text>
            </Position.HeadElement>
            <Position.HeadElement>
              <DatePicker
                compact
                type="range"
                startDateId="startDate"
                endDateId="endDate"
                isOutsideRange={() => false}
                startDate={this.state.startDate}
                endDate={this.state.endDate}
                onDatesChange={this.handleDateChanged}
                focusedInput={this.state.focusedInput}
                keepOpenOnDateSelect
                showPeriods
                onFocusChange={focusedInput => {
                  this.setState({ focusedInput });
                }}
              />
              <Position.Filter>
                <LocationSelectorComponent
                  customFilter={clientUnitFilterOptions}
                  locationOptions={this.props.locationOptions}
                  onLocationChange={this.handleClientFilterChange}
                  selectedLocation={this.state.clientUnitFilter}
                  placeholder="All Clients"
                />
              </Position.Filter>
              <Button
                onClick={this.handleExportClick}
                primary
                disabled={!hasData || this.state.isExporting}
                preloader={this.state.isExporting}
              >
                Export to Excel
              </Button>
            </Position.HeadElement>
          </Position.Head>
          <Position.Body>
            <RunningLineLoader
              isActive={
                this.state.partialShipmentsStatus === "sorting" ||
                this.state.partialShipmentsStatus === "loading"
              }
            />
            <Table
              fullHeight
              columns={[
                { name: "invoiceDate", label: "Date", sortable: true },
                { name: "ottoPoNumber", label: "OTTO PO Number", sortable: true },
                { name: "invoiceNumber", label: "Invoice Number", sortable: true },
                { name: "productSku", label: "Product SKU", sortable: true },
                { name: "vendorName", label: "Vendor", sortable: true },
                { name: "ottoQty", label: "OTTO Qty", sortable: true, alignment: "right" },
                { name: "vendorQty", label: "Vendor Qty", sortable: true, alignment: "right" },
                { name: "quantityDifference", label: "Qty Diff", sortable: true, alignment: "right" },
                { name: "toolbar", label: "", size: "md" }
              ]}
            >
              <Table.Header
                sortBy={this.state.sorting}
                onSortChanged={this.handleSortChanged}
              />
              <Table.Body>
                {isLoaded &&
                  (hasData ? (
                    this.state.partialShipments.map((shipment, index) => (
                      <Table.Row
                        key={`${index}-${shipment.id}`}
                        cells={[
                          shipment.invoiceDate ? moment(shipment.invoiceDate).format("MM/DD/YYYY") : "N/A",
                          shipment.ottoPoNumber,
                          shipment.invoiceNumber || "N/A",
                          shipment.productSku,
                          shipment.vendorName || "N/A",
                          shipment.ottoQty !== null ? shipment.ottoQty : "N/A",
                          shipment.vendorQty !== null ? shipment.vendorQty : "N/A",
                          shipment.quantityDifference !== null ? shipment.quantityDifference : "N/A",
                          <Position.Toolbar key={`cell-toolbar-${index}-${shipment.id}`}>
                            <Button
                              onClick={() => this.handleResolveClick(shipment.id)}
                              disabled={this.state.isResolving[shipment.id]}
                              preloader={this.state.isResolving[shipment.id]}
                            >
                              Resolved
                            </Button>
                          </Position.Toolbar>
                        ]}
                      />
                    ))
                  ) : (
                    <Table.Row
                      key={0}
                      colSpan="9"
                      rows={this.props.perPage}
                      cells={[
                        <span key="0">
                          There are no partial shipments within the parameters.
                        </span>
                      ]}
                    />
                  ))}
                {this.state.partialShipmentsStatus === "loading" &&
                  PartialShipmentsList.renderPreloader(
                    this.props.perPage,
                    this.state.partialShipmentsStatus
                  )}
              </Table.Body>
            </Table>
          </Position.Body>
          <Pagination
            skip={this.state.skip}
            perPage={this.props.perPage}
            count={this.state.totalCount}
            onChange={this.handleChangePage}
          />
        </Position.Container>
      </MainContainer>
    );
  }
}

interface IConnectedProps {
  locationInfo?: IShortLocationInfo;
  locationOptions: IFlatLocationOption[];
  perPage: number;
}

interface IConnectedActions {
  setSearch: typeof actionsSearch.set;
  setPerPage: typeof layoutActions.setPerPage;
  notifySwagger: typeof notifySwagger;
  notify: typeof notify;
}

export default withRouter(
  connect(
    createStructuredSelector<IGlobalStore, IConnectedProps>({
      perPage: state => state.layout.perPage,
      locationInfo: state => state.currentUser.locationInfo,
      locationOptions: state => state.currentUser.locationOptions
    }),
    dispatch =>
      bindActionCreators(
        {
          setSearch: actionsSearch.set,
          setPerPage: layoutActions.setPerPage,
          notifySwagger,
          notify
        },
        dispatch
      )
  )(withGranted(PartialShipmentsList))
);